/**
 * ComposerMentionPills — the draft box confirms a mention before you send it
 * (Miles, 2026-09-22: "when a mention happens in the draft box the same thing
 * must happen … this helps the user know this is a confirmed user handle as
 * well before sending it").
 *
 * A gold pill appears behind an `@handle` the moment it resolves to somebody
 * who will actually be reached, and never appears for one that does not — the
 * same contrast that IS the confirmation in the transcript, arriving while
 * there is still time to fix a spelling.
 *
 * It paints BEHIND the composer's own text and touches nothing: the geometry
 * comes from `Range.getClientRects()` over the live glyphs, so the pill is
 * where the text actually is at any wrapping, any scroll position, and
 * whatever DOM a paste left behind. See composerMentionPills.ts for why this
 * is measured rather than mirrored, and why a chip cannot live inside the
 * composer at all.
 *
 * Inert by construction: `aria-hidden` (the handle is already in the textbox
 * the screen reader reads), `pointer-events: none`, `user-select: none`. It
 * renders nothing at all when no mention resolves, so a draft without one
 * costs a `indexOf('@')`.
 */
import React from 'react';
import styled, { keyframes } from 'styled-components';
import {
  flattenComposerText,
  mentionOffsetsIn,
  pillBoxesFrom,
  pointAt,
  separatePills,
} from '../../services/threadMentions/composerMentionPills';
import type { MentionPillBox } from '../../services/threadMentions/composerMentionPills';
import type { MentionChipPerson } from '../../services/threadMentions/mentionChip';

const settle = keyframes`
  from { opacity: 0; transform: scale(0.82); }
  to   { opacity: 1; transform: scale(1); }
`;

/**
 * A SPAN, not a div, and that is load-bearing: `PromptInputWrapper`'s
 * `& > div` rule hands every div child of the composer wrapper that grid
 * cell's padding, border and grid-area, so a div overlay would draw a second
 * border around the composer. Absolutely positioned, so it is out of the
 * grid's flow entirely and can never add a track.
 */
const Layer = styled.span`
  position: absolute;
  inset: 0;
  display: block;
  overflow: hidden;
  pointer-events: none;
  user-select: none;
  /* Behind the composer's text. Both are positioned, so DOM order decides and
     this element is rendered first; the z-index is belt, not the mechanism. */
  z-index: 0;
  border-radius: 10px;
`;

/**
 * The pill. Gold fill on the composer's light ground with the person's own
 * typed characters showing through in the composer's own dark text — the
 * charcoal-on-gold pairing theme.ts sanctions (6.1:1), and the same solid
 * fill the transcript's chip uses, so "confirmed" looks like one thing across
 * both surfaces.
 */
const Pill = styled.div`
  position: absolute;
  /**
   * A ROUNDED RECTANGLE, where the transcript's chip is a capsule — the one
   * place the two surfaces deliberately differ, and not a style whim.
   *
   * The chip REPLACES a handle with the person's name, so it owns its layout
   * and can pad itself freely. This pill sits behind text it must not move,
   * inside the ~4px a space gives it, and a 999px radius on a ~21px box curves
   * its first and last 10px — putting the '@' and the final letter inside the
   * curve, and leaving nothing between the pill and the next word. A ~6px
   * radius reads as the same family and fits the metrics it was handed.
   */
  border-radius: 6px;
  background: var(--brand-gold);
  box-shadow: inset 0 0 0 1px rgba(43, 43, 43, 0.14);
  animation: ${settle} 0.16s cubic-bezier(0.2, 0.9, 0.3, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export interface ComposerMentionPillsProps {
  /** The composer element whose text is measured. */
  inputRef: React.RefObject<HTMLElement | null>;
  /**
   * The draft, as the host already tracks it. Not read for geometry — the DOM
   * is — but a change to it is the signal to re-measure.
   */
  text: string;
  /** The chip's own resolver: exactly the people a send would reach, or null. */
  resolve?: ((handle: string) => MentionChipPerson | null) | null;
  /** Changes when the roster behind `resolve` changes — the re-measure signal. */
  digest?: string;
}

export function ComposerMentionPills({
  inputRef,
  text,
  resolve,
  digest,
}: ComposerMentionPillsProps): React.ReactElement | null {
  const layerRef = React.useRef<HTMLSpanElement | null>(null);
  const [pills, setPills] = React.useState<MentionPillBox[]>([]);

  // Live handles, so the measure callback stays stable and the effect below
  // does not resubscribe on every keystroke.
  const resolveRef = React.useRef(resolve);
  resolveRef.current = resolve;

  const measure = React.useCallback(() => {
    const layer = layerRef.current;
    const host = inputRef.current;
    const resolveNow = resolveRef.current;
    if (!layer || !host || !resolveNow) {
      setPills((prev) => (prev.length ? [] : prev));
      return;
    }
    let next: MentionPillBox[] = [];
    try {
      const flat = flattenComposerText(host);
      if (flat.text.indexOf('@') !== -1) {
        const base = layer.getBoundingClientRect();
        for (const span of mentionOffsetsIn(flat.text, resolveNow)) {
          const from = pointAt(flat.segments, span.start);
          const to = pointAt(flat.segments, span.end);
          if (!from || !to) continue;
          const range = document.createRange();
          range.setStart(from.node, from.offset);
          range.setEnd(to.node, to.offset);
          next = next.concat(pillBoxesFrom(Array.from(range.getClientRects()), base, span));
        }
      }
    } catch {
      // A measurement that cannot be taken paints nothing. Never a guessed
      // pill: a pill in the wrong place is worse than no pill.
      next = [];
    }
    // "@Prest0n @malaika" is one space apart; without this the two pills
    // overlap and paint as a single bar.
    next = separatePills(next);
    setPills((prev) => (samePills(prev, next) ? prev : next));
  }, [inputRef]);

  /**
   * One frame-batched measurement, stable for the life of the component.
   *
   * Stable is the point: the subscription effect below binds listeners ONCE,
   * where a callback rebuilt per keystroke would add and remove three
   * listeners and a ResizeObserver on every character typed — on the hottest
   * path in the product.
   */
  const frameRef = React.useRef(0);
  const schedule = React.useCallback(() => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      measure();
      return;
    }
    if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = 0;
      measure();
    });
  }, [measure]);

  // Anything that can move the glyphs WITHOUT changing them: the composer
  // scrolling under its own max-height, the pane resizing, the window
  // resizing. Bound once — React attaches refs before effects run, and the
  // composer is a sibling in the same commit, so the element is here.
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const host = inputRef.current;
    host?.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver === 'function' && host) {
      observer = new ResizeObserver(schedule);
      observer.observe(host);
    }
    return () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      host?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [schedule, inputRef]);

  // …and the things that DO change them: the draft, and the roster behind the
  // resolver.
  React.useEffect(() => {
    schedule();
  }, [schedule, text, digest]);

  if (pills.length === 0) {
    // Still mounted: the layer has to exist for the next measurement to have a
    // box to be relative to.
    return <Layer ref={layerRef} aria-hidden="true" data-composer-mention-pills="" />;
  }

  return (
    <Layer ref={layerRef} aria-hidden="true" data-composer-mention-pills="">
      {pills.map((pill, i) => (
        <Pill
          // The handle and the position together: a pill that moves keeps its
          // identity (no re-entry animation on every keystroke), and a pill
          // for a different handle is a different element.
          key={`${pill.handle}:${i}`}
          data-mention-pill={pill.handle}
          style={{
            left: `${pill.left}px`,
            top: `${pill.top}px`,
            width: `${pill.width}px`,
            height: `${pill.height}px`,
          }}
        />
      ))}
    </Layer>
  );
}

/** Cheap structural equality, so a measurement that changed nothing re-renders nothing. */
function samePills(a: readonly MentionPillBox[], b: readonly MentionPillBox[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (x.handle !== y.handle || x.left !== y.left || x.top !== y.top
      || x.width !== y.width || x.height !== y.height) return false;
  }
  return true;
}

export default ComposerMentionPills;
